import React, { useCallback, useEffect, useState } from 'react';
import { formatMoney } from '../../../utils/money';
import { formatDateCa } from '../legal-board/legalBoardUi';
import { Disclosure } from '../legal-board/LegalBoardPrimitives';
import { StepScreen } from './PublicUtilityForms';
import {
    fetchDonors,
    saveIdentity,
    updateSettings,
    addManualDonation,
    deleteManualDonation,
    downloadModel182,
    downloadCertificates,
} from './api';

const euros = (value) => `${formatMoney(value)} €`;

const payerTarget = (payer) => (payer.source === 'casteller' ? { castellerId: payer.id } : payer.source === 'sponsor' ? { sponsorId: payer.id } : { identityId: payer.id });

function IdentityForm({ id, initial = {}, canalla = false, withDonation = false, busy, onSubmit, onCancel = null, submitLabel = 'Desar', submitClass = 'primary' }) {
    const [form, setForm] = useState({
        legalName: initial.legalName || '',
        nif: initial.nif || '',
        postalCode: initial.postalCode || '',
        amount: '',
        donatedOn: '',
        note: '',
    });
    const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
    // The 182 asks for the province of the donor's home, which the postal code gives
    const complete = form.legalName.trim() && form.nif.trim() && /^\d{5}$/.test(form.postalCode.trim()) && (!withDonation || (form.amount && form.donatedOn));
    return (
        <div className="legal-board-inline-form public-utility-identity-form">
            {canalla ? <p className="legal-board-muted legal-board-small">És un infant: posa-hi les dades de qui paga i ho desgrava (mare, pare o tutor).</p> : null}
            <div className="legal-board-form-grid">
                <div className="legal-board-field">
                    <label htmlFor={`${id}-name`}>Cognoms i nom (o raó social)</label>
                    <input id={`${id}-name`} type="text" value={form.legalName} onChange={set('legalName')} disabled={busy} autoComplete="off" />
                </div>
                <div className="legal-board-field">
                    <label htmlFor={`${id}-nif`}>DNI, NIE o NIF</label>
                    <input id={`${id}-nif`} type="text" value={form.nif} onChange={set('nif')} disabled={busy} autoComplete="off" spellCheck={false} />
                </div>
                <div className="legal-board-field">
                    <label htmlFor={`${id}-postal`}>Codi postal</label>
                    <input id={`${id}-postal`} type="text" inputMode="numeric" value={form.postalCode} onChange={set('postalCode')} disabled={busy} />
                </div>
                {withDonation ? (
                    <>
                        <div className="legal-board-field">
                            <label htmlFor={`${id}-amount`}>Import (€)</label>
                            <input id={`${id}-amount`} type="text" inputMode="decimal" value={form.amount} onChange={set('amount')} disabled={busy} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor={`${id}-date`}>Data</label>
                            <input id={`${id}-date`} type="date" value={form.donatedOn} onChange={set('donatedOn')} disabled={busy} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor={`${id}-note`}>Concepte (opcional)</label>
                            <input id={`${id}-note`} type="text" value={form.note} onChange={set('note')} disabled={busy} />
                        </div>
                    </>
                ) : null}
            </div>
            <div className="legal-board-inline-form-actions">
                <button type="button" className={`legal-board-button ${submitClass}`} onClick={() => onSubmit(form)} disabled={busy || !complete}>{submitLabel}</button>
                {onCancel ? <button type="button" className="legal-board-button link" onClick={onCancel} disabled={busy}>Cancel·lar</button> : null}
            </div>
        </div>
    );
}

const SCREENS = ['settings', 'missing', 'manual', 'review'];

/**
 * The year's donors for the Model 182, one screen at a time: what counts as a
 * donation, the DNIs that are missing (one donor after another), donations
 * from outside Aleta, and the review with the file and the certificates.
 * `startAt` opens it where the deadline screen sent you.
 */
function PublicUtilityDonors({ userId, year, collaNif, canManage, startAt = 'settings', onBack }) {
    const [screen, setScreen] = useState(Math.max(0, SCREENS.indexOf(startAt)));
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [problems, setProblems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [skipped, setSkipped] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [contact, setContact] = useState(null);

    const load = useCallback(async () => {
        try {
            const payload = await fetchDonors({ userId, year });
            setData(payload);
            setContact((current) => current || payload.declarant || { contactName: '', phone: '' });
        } catch (loadError) {
            setError(loadError.message || 'No s’han pogut carregar els donants.');
        }
    }, [userId, year]);

    useEffect(() => {
        load();
    }, [load]);

    const run = async (action, { reload = true } = {}) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        setProblems([]);
        try {
            await action();
            setEditing(null);
            if (reload) await load();
        } catch (actionError) {
            setError(actionError.message || 'No s’ha pogut completar.');
            setProblems(Array.isArray(actionError.payload?.details) ? actionError.payload.details : []);
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <section className="legal-board-card">
                <button type="button" className="legal-board-button link" onClick={onBack}>← Tornar</button>
                {error ? <div className="legal-board-alert error" role="alert">{error}</div> : <div className="legal-board-spinner" role="status" aria-label="Carregant"></div>}
            </section>
        );
    }

    const { totals, settings, donors, manualDonations } = data;
    const missing = donors.filter((donor) => donor.status !== 'ok');
    const pending = missing.filter((donor) => !skipped.includes(donor.key));
    const declared = donors.filter((donor) => donor.status === 'ok');
    // A DNI without a postal code: the 182 asks for the province of the donor's home
    const lacking = missing.some((donor) => donor.status === 'no_postal_code') ? 'DNI o codi postal' : 'DNI';
    const errorBox = error ? (
        <div className="legal-board-alert error" role="alert">
            {error}
            {problems.length > 0 ? <ul>{problems.map((problem, index) => <li key={`${problem.code}-${index}`}>{problem.message}</li>)}</ul> : null}
        </div>
    ) : null;
    const frame = (index, title, props, children) => (
        <StepScreen id="public-utility-donors" title={title} step={index + 1} total={SCREENS.length} onExit={onBack} busy={busy}
            onBack={index > 0 ? () => setScreen(index - 1) : null} {...props}>
            {children}
            {errorBox}
        </StepScreen>
    );

    const saveForPayers = (donor, form) => run(async () => {
        // Every payer under this donor (a parent paying for two children) gets the same identity
        for (const payer of donor.payers) {
            await saveIdentity({ userId, identity: { ...payerTarget(payer), nif: form.nif, legalName: form.legalName, postalCode: form.postalCode } });
        }
    });

    if (SCREENS[screen] === 'settings') {
        return frame(0, `Què compta com a donatiu el ${year}`, { onNext: () => setScreen(1) }, (
            <>
                <div className="public-utility-settings" role="group" aria-label="Què compta com a donatiu">
                    <label>
                        <input type="checkbox" checked={settings.includeMemberFees} disabled={!canManage || busy} onChange={(event) => run(() => updateSettings({ userId, settings: { includeMemberFees: event.target.checked } }))} />
                        <span>Les quotes de soci. <span className="legal-board-muted">Si la quota no dona dret a res a canvi, la Llei 49/2002 la tracta com un donatiu.</span></span>
                    </label>
                    <label>
                        <input type="checkbox" checked={settings.includeSponsorPayments} disabled={!canManage || busy} onChange={(event) => run(() => updateSettings({ userId, settings: { includeSponsorPayments: event.target.checked } }))} />
                        <span>El que paguen els patrocinadors. <span className="legal-board-muted">Només si no reben res a canvi, ni publicitat.</span></span>
                    </label>
                </div>
                <p className="legal-board-muted">Els donatius de més, a sobre de la quota o del patrocini, sempre compten. Ara mateix: {totals.donors} {totals.donors === 1 ? 'donant' : 'donants'} · {euros(totals.amount)}.</p>
            </>
        ));
    }

    if (SCREENS[screen] === 'missing') {
        const donor = pending[0];
        if (!donor || !canManage) {
            return frame(1, lacking === 'DNI' ? 'DNI dels donants' : 'Dades dels donants', { onNext: () => setScreen(2) }, (
                <p>
                    {missing.length === 0 ? `Els ${totals.donors} donants tenen DNI.`
                        : `Queden ${missing.length} ${missing.length === 1 ? 'donant' : 'donants'} sense ${lacking}, que no aniran al Model 182.`}
                    {missing.length > 0 && canManage ? <> <button type="button" className="legal-board-inline-action" onClick={() => setSkipped([])}>Tornar-hi</button>.</> : null}
                </p>
            ));
        }
        const position = missing.length - pending.length + 1;
        const names = donor.payers.map((payer) => payer.name).filter(Boolean).join(', ');
        const suggestion = donor.payers[0]?.suggestion;
        const onlyPostal = donor.status === 'no_postal_code';
        return frame(1, lacking === 'DNI' ? 'DNI que falten' : 'Dades que falten', { onNext: () => setScreen(2) }, (
            <>
                <p className="legal-board-muted">{position} de {missing.length} · sense {onlyPostal ? 'codi postal' : 'DNI'} no van al Model 182 i el donant no ho desgrava.</p>
                <p><strong>{names || donor.legalName || 'Sense nom'}</strong> · {euros(donor.amount)}</p>
                {onlyPostal ? <p className="legal-board-small">Té el DNI. Falta el codi postal de casa seva: el Model 182 en demana la província.</p> : null}
                <IdentityForm
                    key={donor.key}
                    id={`public-utility-missing-${donor.key}`}
                    initial={onlyPostal ? { legalName: donor.legalName, nif: donor.nif, postalCode: suggestion?.postalCode || '' } : suggestion || {}}
                    canalla={Boolean(suggestion?.canalla)}
                    busy={busy}
                    submitLabel={onlyPostal ? 'Desar el codi postal' : 'Desar el DNI'}
                    submitClass="secondary"
                    onSubmit={(form) => saveForPayers(donor, form)}
                />
                <p className="legal-board-small">
                    <button type="button" className="legal-board-inline-action" onClick={() => setSkipped((current) => [...current, donor.key])} disabled={busy}>Saltar aquest donant</button>
                </p>
            </>
        ));
    }

    if (SCREENS[screen] === 'manual') {
        return frame(2, 'Donatius de fora d’Aleta', { onNext: () => setScreen(3) }, (
            <>
                <p className="legal-board-muted">Transferències o efectiu que no han passat per Aleta. Si no n’hi ha cap, continua.</p>
                {manualDonations.length > 0 ? (
                    <ul className="public-utility-tap-list" aria-label="Donatius de fora d’Aleta">
                        {manualDonations.map((item) => (
                            <li key={item.id} className="public-utility-manual-row">
                                <span className="public-utility-tap-text">
                                    <strong>{item.legalName}</strong>
                                    <span className="public-utility-tap-sub">{formatDateCa(item.donatedOn)}{item.note ? ` · ${item.note}` : ''}</span>
                                </span>
                                <span className="public-utility-tap-value">{euros(item.amount)}</span>
                                {canManage ? (
                                    <button type="button" className="legal-board-icon-button" aria-label={`Treure el donatiu de ${item.legalName}`} onClick={() => run(() => deleteManualDonation({ userId, id: item.id }))} disabled={busy}>
                                        <i className="fas fa-trash-alt" aria-hidden="true"></i>
                                    </button>
                                ) : <span></span>}
                            </li>
                        ))}
                    </ul>
                ) : null}
                {canManage ? (
                    editing === 'manual' ? (
                        <IdentityForm id="public-utility-manual" withDonation submitLabel="Afegir el donatiu" busy={busy} onSubmit={(form) => run(() => addManualDonation({ userId, donation: form }))} onCancel={() => setEditing(null)} />
                    ) : (
                        <p><button type="button" className="legal-board-inline-action" onClick={() => setEditing('manual')} disabled={busy}>+ Afegir un donatiu</button></p>
                    )
                ) : null}
            </>
        ));
    }

    return frame(3, `Model 182 de ${year}`, canManage ? {
        onNext: () => run(() => downloadModel182({ userId, year, nif: collaNif, ...contact }), { reload: false }),
        nextLabel: 'Descarregar el fitxer',
        nextIcon: 'fa-download',
        nextDisabled: totals.declared === 0,
    } : {}, (
        <>
            <p>
                Hi aniran <strong>{totals.declared} {totals.declared === 1 ? 'donant' : 'donants'}</strong> amb {euros(totals.declaredAmount)}.
                {missing.length > 0 ? <> En {missing.length === 1 ? 'queda 1' : `queden ${missing.length}`} fora per no tenir {lacking} (<button type="button" className="legal-board-inline-action" onClick={() => { setSkipped([]); setScreen(1); }}>completar-los</button>).</> : null}
            </p>
            <Disclosure id="public-utility-donor-list" summary={`Veure els ${declared.length} donants`} open={showAll} onToggle={() => setShowAll((value) => !value)}>
                <ul className="public-utility-tap-list" aria-label="Donants">
                    {declared.map((donor) => (
                        <li key={donor.key}>
                            <button type="button" className="public-utility-tap-row" onClick={() => setEditing(editing === donor.key ? null : donor.key)} disabled={!canManage || busy}>
                                <span className="public-utility-tap-text">
                                    <strong>{donor.legalName}</strong>
                                    <span className="public-utility-tap-sub">{donor.nif}{donor.recurrent && donor.deductionRate !== 80 ? ` · recurrent (${donor.deductionRate}%)` : ''}</span>
                                </span>
                                <span className="public-utility-tap-value">{euros(donor.amount)}</span>
                                {canManage ? <i className={`fas fa-chevron-${editing === donor.key ? 'down' : 'right'}`} aria-hidden="true"></i> : <span></span>}
                            </button>
                            {editing === donor.key ? (
                                <>
                                    <IdentityForm id={`public-utility-edit-${donor.key}`} initial={{ legalName: donor.legalName, nif: donor.nif, postalCode: donor.postalCode }} busy={busy} onSubmit={(form) => saveForPayers(donor, form)} onCancel={() => setEditing(null)} />
                                    <p className="legal-board-small">
                                        <button type="button" className="legal-board-inline-action" onClick={() => run(() => downloadCertificates({ userId, year, key: donor.key }), { reload: false })} disabled={busy}>Certificat de {donor.legalName} (PDF)</button>
                                    </p>
                                </>
                            ) : null}
                        </li>
                    ))}
                </ul>
            </Disclosure>
            {canManage ? (
                <>
                    <div className="legal-board-form-grid">
                        <div className="legal-board-field">
                            <label htmlFor="public-utility-contact">Persona de contacte (cognoms i nom)</label>
                            <input id="public-utility-contact" type="text" value={contact?.contactName || ''} onChange={(event) => setContact((current) => ({ ...current, contactName: event.target.value }))} disabled={busy} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor="public-utility-phone">Telèfon</label>
                            <input id="public-utility-phone" type="tel" value={contact?.phone || ''} onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))} disabled={busy} />
                        </div>
                    </div>
                    <p className="legal-board-muted legal-board-small">
                        Després, a la seu de l’Agència Tributària: Model 182 › «Presentación mediante fichero», amb el certificat digital de la colla.
                        {' '}Si algun donant et demana el certificat,{' '}
                        <button type="button" className="legal-board-inline-action" onClick={() => run(() => downloadCertificates({ userId, year }), { reload: false })} disabled={busy || totals.declared === 0}>descarrega’ls tots</button>.
                    </p>
                </>
            ) : null}
        </>
    ));
}

export default PublicUtilityDonors;
